import { useRef, useState } from "react";
import { Box, Typography, MenuItem, Select, Button } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { updateFields } from "../../../redux/profileSlice";
import RoundedTextField from "../../../components/RoundedTextField";
import { baseFetch } from "../../../helpers/apiClient";
import CheckIcon from "@mui/icons-material/CheckCircle";
import phoneCode from "../../../constants/country_dial_info.json";
import RoundedPhoneTextField from "../../../components/RoundedPhoneTextField";
import ImageCropper from "../../../components/ImageCropper";
import Loader from "../../../components/Loader";
import { toggleSessionModal } from "../../../redux/sessionSlice";
import CircularImageCropper from "../../../components/CircularImageCropper";
import { getPhoneParts } from "../../../helpers/phone";

export default function BasicDetailsEditProfile({ localProfile }) {
  const profileInfo = useSelector((state) => state.profileSlice.profile);
  const updatedFields = useSelector(
    (state) => state.profileSlice?.updatedFields,
  );
  const phone = updatedFields?.phone ?? localProfile.profile?.phone ?? "";
  const { countryCode, number: phoneNumber } = getPhoneParts(phone);
  const [tempPreview, setTempPreview] = useState(null);
  const [imageCropped, setImageCropped] = useState(null);
  const [openImageCropper, setOpenImageCropper] = useState(false);
  const fileInputRef = useRef(null);
  const role = profileInfo.roles?.join("_");
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  const handleInputChange = (e) => {
    let { name, value } = e.target;
    console.log("name is ", name);
    console.log("value in line37 is", value);
    if (name === "phone") {
      value = value.replace(/\D/g, "");
      if (value.length > 10) {
        value = value.slice(0, 10);
      }
      console.log("value is", value);
      const formattedPhone = `${countryCode} ${value}`;
      dispatch(updateFields({ field: "phone", value: formattedPhone }));
    } else if (name === "sponsorProfile.companyName") {
      dispatch(updateFields({ field: name, value: value }));
    } else {
      dispatch(
        updateFields({ field: name, value: value.replace(/[^a-zA-Z\s]/g, "") }),
      );
    }
  };
  const handleFileChange = async (event) => {
    setOpenImageCropper(true);
    const images = event.target.files[0];
    setImageCropped(URL.createObjectURL(images));
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleImageUpload = async (file) => {
    setLoading(true);
    setTempPreview(URL.createObjectURL(file));
    const formData = new FormData();
    formData.append("photo", file);
    const payload = formData;
    let photoUrl;
    await baseFetch({
      url: `/v1/web/upload/image`,
      method: "POST",
      body: payload,
    })
      .then((response) => {
        if (response.status) {
          console.log("url is", response.data.upload.url);
          setLoading(false);
          photoUrl = response.data.upload.url;
        }
      })
      .catch((err) => {
        setLoading(false);
        if (err === "Please authenticate") {
          dispatch(toggleSessionModal(false));
        }
      });
    if (role !== "sponsor") {
      dispatch(updateFields({ field: "photo", value: photoUrl }));
    } else {
      dispatch(updateFields({ field: "sponsorProfile.logo", value: photoUrl }));
    }
    setTempPreview(photoUrl);
    setOpenImageCropper(false);
  };

  const handleDialogClose = () => {
    setOpenImageCropper(false);
  };

  return (
    <div>
      {console.log("loading", loading)}
      <Box sx={{ zIndex: "99" }}>{loading && <Loader />}</Box>
      <Box
        sx={{
          display: {
            xs: "flex",
            sm: "flex",
          },
          alignItems: "center",
          gap: "12px",
          marginTop: "10px",
          marginBottom: {
            xs: "15px",
            sm: 0,
          },
          textAlign: {
            xs: "left",
            sm: "center",
          },
        }}
      >
        <Box
          sx={{
            width: {
              xs: "180px",
              sm: "180px",
              md: "180px",
              lg: "180px",
              xl: "200px",
            },
            // Sponsors get a circular frame to match the corporate-logo treatment
            // everywhere else (signup, industry hub, event listings). Non-sponsor
            // profiles keep the original portrait-photo rectangle.
            height:
              role === "sponsor"
                ? {
                    xs: "180px",
                    sm: "180px",
                    md: "180px",
                    lg: "180px",
                    xl: "200px",
                  }
                : {
                    xs: "240px",
                    sm: "240px",
                    md: "240px",
                    lg: "240px",
                    xl: "250px",
                  },
            borderRadius: role === "sponsor" ? "50%" : "30px",
            overflow: "hidden",
            ...(role === "sponsor" && {
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }),
          }}
        >
          <img
            src={
              role !== "sponsor"
                ? tempPreview || localProfile.profile?.photo
                : tempPreview || localProfile.profile?.sponsorProfile?.logo
            }
            width="100%"
            alt="Profile"
            style={{ objectFit: "cover" }}
          />
        </Box>
        <input
          type="file"
          id="imageUpload"
          accept="image/jpg,image/jpeg,image/png"
          style={{ display: "none" }}
          onChange={handleFileChange}
          ref={fileInputRef}
        />
        <label htmlFor="imageUpload">
          <Button
            style={{
              border: "1px solid #AAB6C8",
              padding: "10px 26px",
              borderRadius: "24px",
              textTransform: "none",
            }}
            component="span"
          >
            {" "}
            Change
          </Button>
        </label>
        {role !== "sponsor" && imageCropped && (
          <ImageCropper
            open={openImageCropper}
            image={imageCropped}
            onCrop={handleImageUpload}
            onCancel={handleDialogClose}
            loading={loading}
          />
        )}

        {role === "sponsor" && imageCropped && (
          <CircularImageCropper
            open={openImageCropper}
            image={imageCropped}
            onCrop={handleImageUpload}
            onCancel={handleDialogClose}
            aspectRatio={1}
          />
        )}
      </Box>
      <div style={{ marginTop: "10px" }}>
        <Typography mb={0.5} sx={{ textAlign: "left" }} component="dt">
          First Name
        </Typography>
        <RoundedTextField
          fullWidth
          size="small"
          name="firstName"
          onChange={handleInputChange}
          value={
            updatedFields?.firstName || localProfile.profile?.firstName || ""
          }
          sx={{
            marginBottom: "12px",
            borderRadius: "50px",
            backgroundColor: "#F2F7FB",
            textTransform: "capitalize",
            "& .MuiInputBase-input": {
              color: "#2A4876",
            },
          }}
        />
        <Typography mb={0.5} sx={{ textAlign: "left" }} component="dt">
          Last Name
        </Typography>
        <RoundedTextField
          fullWidth
          size="small"
          name="lastName"
          onChange={handleInputChange}
          value={
            updatedFields?.lastName || localProfile.profile?.lastName || ""
          }
          sx={{
            marginBottom: "12px",
            borderRadius: "50px",
            backgroundColor: "#F2F7FB",
            textTransform: "capitalize",
            "& .MuiInputBase-input": {
              color: "#2A4876",
            },
          }}
        />
        {role !== "sponsor" && (
          <>
            <Typography mb={0.5} sx={{ textAlign: "left" }} component="dt">
              Mobile Number
            </Typography>
            <RoundedPhoneTextField
              name="phone"
              variant="outlined"
              placeholder="Enter phone number"
              fullWidth
              value={phoneNumber}
              onChange={handleInputChange}
              sx={{
                border: "1px solid #2A4876",
                textTransform: "capitalize",
                marginBottom: "10px",
                "& .MuiInputBase-input": {
                  color: "#2A4876",
                  padding: "9px 10px",
                },
              }}
              slotProps={{
                input: {
                  maxLength: 10,
                  startAdornment: (
                    <Select
                      value={countryCode}
                      label="Age"
                      onChange={(e) => {
                        const newCountryCode = e.target.value;
                        const formattedPhone = `${newCountryCode} ${phoneNumber}`;
                        dispatch(
                          updateFields({
                            field: "phone",
                            value: formattedPhone,
                          }),
                        );
                      }}
                    >
                      {phoneCode &&
                        phoneCode.map((option) => (
                          <MenuItem
                            key={option.dial_code}
                            value={option.dial_code}
                          >
                            {option.dial_code}{" "}
                            {countryCode === option.dial_code && (
                              <CheckIcon
                                sx={{
                                  marginLeft: "auto",
                                  color: "#2A4876",
                                  width: "14px",
                                }}
                              />
                            )}
                          </MenuItem>
                        ))}
                    </Select>
                  ),
                  "aria-label": "country code input",
                },
              }}
            />
          </>
        )}
        {role === "sponsor" && (
          <>
            <Typography
              mb={0.5}
              sx={{ fontSize: "14px", textAlign: "left" }}
              component="dt"
            >
              Company Name
            </Typography>
            <RoundedTextField
              fullWidth
              size="small"
              value={
                updatedFields?.sponsorProfile?.companyName ||
                localProfile.profile?.sponsorProfile?.companyName ||
                ""
              }
              sx={{
                marginBottom: "12px",
                borderRadius: "50px",
                backgroundColor: "#F2F7FB",
                textTransform: "capitalize",
                "& .MuiInputBase-input": {
                  color: "#2A4876",
                },
              }}
              name="sponsorProfile.companyName"
              onChange={handleInputChange}
            />
          </>
        )}
      </div>
    </div>
  );
}
